import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Smartphone, Plus, Trash2, Sparkles, Loader2, CheckCircle2,
  XCircle, MapPin, Star, Eye, FileText, Clock, User
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_CATEGORIES = [
  "Ogolne", "Wydarzenie", "Wypadek", "Otwarcie", "Zamkniecie",
  "Kultura", "Sport", "Infrastruktura", "Przyroda", "Spolecznosc"
];

const statusConfig = {
  new: { label: "Nowy", bg: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  reviewed: { label: "Przegladany", bg: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  approved: { label: "Zatwierdzony", bg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  published: { label: "Opublikowany", bg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  rejected: { label: "Odrzucony", bg: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" },
};

export default function MobileReports() {
  const { t } = useLanguage();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [suggestingId, setSuggestingId] = useState(null);
  const [form, setForm] = useState({
    headline: "", content: "", location: "", category: "Ogolne", reporter_name: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reports`),
        axios.get(`${API}/reports/stats`),
      ]);
      setReports(reportsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.headline.trim()) return;
    try {
      await axios.post(`${API}/reports`, form);
      toast.success(t("common.success"));
      fetchData(); setDialogOpen(false);
      setForm({ headline: "", content: "", location: "", category: "Ogolne", reporter_name: "" });
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleSuggest = async (id) => {
    setSuggestingId(id);
    try {
      const res = await axios.post(`${API}/reports/${id}/suggest`);
      toast.success(t("reports.suggested"));
      fetchData();
      if (selectedReport?.id === id) setSelectedReport(r => ({ ...r, ai_suggestions: res.data, status: "reviewed" }));
    } catch (err) { toast.error(t("common.error")); }
    finally { setSuggestingId(null); }
  };

  const handleStatus = async (id, status, points) => {
    try {
      const update = { status };
      if (points !== undefined) update.points = points;
      await axios.patch(`${API}/reports/${id}`, update);
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/reports/${id}`);
      if (selectedReport?.id === id) setSelectedReport(null);
      toast.success(t("common.success")); fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("reports.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("reports.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="submit-report-btn" className="rounded-lg gap-2">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("reports.submitReport")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{t("reports.submitReport")}</DialogTitle></DialogHeader>
            <div className="grid gap-3 py-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("reports.headline")}</Label>
                <Input data-testid="report-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("reports.content")}</Label>
                <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={3} className="resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("reports.location")}</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Warszawa" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("reports.category")}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{REPORT_CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("reports.reporter")}</Label>
                <Input value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("sites.cancel")}</Button>
              <Button data-testid="save-report-btn" onClick={handleSubmit}>{t("reports.submit")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold font-heading text-foreground">{stats.total}</p>
              <p className="text-[10px] text-muted-foreground">{t("reports.totalReports")}</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold font-heading text-blue-500">{stats.new}</p>
              <p className="text-[10px] text-muted-foreground">{t("reports.newReports")}</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold font-heading text-emerald-500">{stats.approved}</p>
              <p className="text-[10px] text-muted-foreground">{t("reports.approvedReports")}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <div className={selectedReport ? "lg:col-span-1" : "lg:col-span-3"}>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {reports.length === 0 ? (
                <div data-testid="no-reports" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Smartphone className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("reports.noReports")}</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[500px]">
                  <div className="divide-y divide-border">
                    {reports.map((r) => {
                      const st = statusConfig[r.status] || statusConfig.new;
                      return (
                        <button key={r.id} data-testid={`report-${r.id}`} onClick={() => setSelectedReport(r)}
                          className={`w-full flex items-start gap-3 p-3 text-left transition-all hover:bg-accent/30 ${selectedReport?.id === r.id ? "bg-primary/5" : ""}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{r.headline}</p>
                            <div className="flex items-center gap-1.5 mt-1">
                              <Badge className={`text-[9px] border-0 rounded-md ${st.bg}`}>{st.label}</Badge>
                              <Badge variant="outline" className="text-[9px] rounded-md">{r.category}</Badge>
                              {r.location && <span className="text-[9px] text-muted-foreground flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{r.location}</span>}
                            </div>
                          </div>
                          {r.points > 0 && (
                            <div className="flex items-center gap-0.5 shrink-0">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              <span className="text-xs font-bold text-amber-500">{r.points}</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail */}
        {selectedReport && (
          <div className="lg:col-span-2">
            <Card data-testid="report-detail" className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-sm font-heading text-foreground">{selectedReport.headline}</CardTitle>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDelete(selectedReport.id)}>
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 pt-2 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] border-0 rounded-md ${(statusConfig[selectedReport.status] || statusConfig.new).bg}`}>
                    {(statusConfig[selectedReport.status] || statusConfig.new).label}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] rounded-md">{selectedReport.category}</Badge>
                  {selectedReport.location && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><MapPin className="w-3 h-3" />{selectedReport.location}</span>
                  )}
                  {selectedReport.reporter_name && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><User className="w-3 h-3" />{selectedReport.reporter_name}</span>
                  )}
                </div>

                {selectedReport.content && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-foreground/80 leading-relaxed">{selectedReport.content}</p>
                  </div>
                )}

                {/* AI Sugestie */}
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-foreground">{t("reports.aiSuggestions")}</p>
                  <Button size="sm" data-testid="suggest-report-btn" onClick={() => handleSuggest(selectedReport.id)}
                    disabled={suggestingId === selectedReport.id} className="h-7 text-xs gap-1 rounded-md">
                    {suggestingId === selectedReport.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" strokeWidth={1.5} />}
                    {t("reports.getSuggestions")}
                  </Button>
                </div>

                {selectedReport.ai_suggestions && (
                  <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                    <div className="text-xs">
                      <span className="font-medium text-muted-foreground">{t("reports.suggestedTitle")}:</span>
                      <span className="text-foreground ml-1">{selectedReport.ai_suggestions.article_title}</span>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-muted-foreground">{t("reports.suggestedStyle")}:</span>
                      <span className="text-foreground ml-1">{selectedReport.ai_suggestions.style}</span>
                    </div>
                    {selectedReport.ai_suggestions.keywords?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {selectedReport.ai_suggestions.keywords.map((kw, i) => (
                          <Badge key={i} variant="outline" className="text-[9px] rounded-md">{kw}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <Separator />

                <div className="flex items-center gap-2 flex-wrap">
                  {selectedReport.status === "new" && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-emerald-600"
                      onClick={() => handleStatus(selectedReport.id, "approved", 10)}>
                      <CheckCircle2 className="w-3 h-3" /> {t("reports.approve")} (+10 pkt)
                    </Button>
                  )}
                  {selectedReport.status === "reviewed" && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-emerald-600"
                      onClick={() => handleStatus(selectedReport.id, "approved", 10)}>
                      <CheckCircle2 className="w-3 h-3" /> {t("reports.approve")} (+10 pkt)
                    </Button>
                  )}
                  {selectedReport.status !== "rejected" && selectedReport.status !== "published" && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-500"
                      onClick={() => handleStatus(selectedReport.id, "rejected")}>
                      <XCircle className="w-3 h-3" /> {t("reports.reject")}
                    </Button>
                  )}
                </div>

                <p className="text-[9px] text-muted-foreground/60">
                  {new Date(selectedReport.created_at).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
